import type { JSONContent } from "@tiptap/core";

const TEXT_BLOCK_TYPES = new Set(["heading", "paragraph"]);

function cloneNode(node: JSONContent): JSONContent {
  return {
    ...node,
    attrs: node.attrs ? { ...node.attrs } : node.attrs,
    content: node.content?.map(cloneNode),
    marks: node.marks?.map((mark) => ({ ...mark })),
  };
}

function collectTextBlocks(node: JSONContent, blocks: JSONContent[]) {
  if (node.type && TEXT_BLOCK_TYPES.has(node.type)) {
    blocks.push(node);
    return;
  }

  node.content?.forEach((child) => collectTextBlocks(child, blocks));
}

function splitPolishedTextIntoBlocks(text: string, expectedBlockCount: number) {
  const normalized = text
    .replace(/\r\n/g, "\n")
    .replace(/[^\S\n]+/g, " ")
    .trim();

  if (!normalized) {
    return [""];
  }

  const paragraphBlocks = normalized
    .split(/\n{2,}/)
    .map((block) => block.replace(/\n+/g, " ").trim())
    .filter(Boolean);

  if (paragraphBlocks.length === expectedBlockCount) {
    return paragraphBlocks;
  }

  const lineBlocks = normalized
    .split(/\n+/)
    .map((block) => block.trim())
    .filter(Boolean);

  return lineBlocks.length === expectedBlockCount ? lineBlocks : paragraphBlocks;
}

function firstTextMarks(node: JSONContent) {
  const stack = [...(node.content ?? [])];
  while (stack.length > 0) {
    const current = stack.shift();
    if (!current) {
      continue;
    }

    if (current.type === "text") {
      return current.marks?.map((mark) => ({ ...mark }));
    }

    if (current.content) {
      stack.unshift(...current.content);
    }
  }

  return undefined;
}

function replaceTextBlockContent(node: JSONContent, text: string) {
  const marks = firstTextMarks(node);

  node.content = text
    ? [
        {
          marks,
          text,
          type: "text",
        },
      ]
    : undefined;
}

export function plainTextToDocument(text: string): JSONContent {
  const paragraphs = text
    .split(/\n{2,}/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean);

  return {
    content: (paragraphs.length > 0 ? paragraphs : [""]).map((paragraph) => ({
      content: paragraph
        ? [
            {
              text: paragraph,
              type: "text",
            },
          ]
        : undefined,
      type: "paragraph",
    })),
    type: "doc",
  };
}

export function replaceDocumentTextPreservingStructure(
  document: JSONContent,
  text: string,
) {
  const nextDocument = cloneNode(document);
  const textBlocks: JSONContent[] = [];
  collectTextBlocks(nextDocument, textBlocks);

  if (textBlocks.length === 0) {
    return plainTextToDocument(text);
  }

  const replacementBlocks = splitPolishedTextIntoBlocks(text, textBlocks.length);
  if (replacementBlocks.length !== textBlocks.length) {
    return plainTextToDocument(text);
  }

  textBlocks.forEach((block, index) => {
    replaceTextBlockContent(block, replacementBlocks[index] ?? "");
  });

  return nextDocument;
}
